import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, CheckCircle2, Sparkles, Mail } from "lucide-react";
import useSeo from "../lib/useSeo";
import { trackPageView } from "../lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  {
    id: "ideas_listed",
    eyebrow: "Question 01 of 05",
    label: "List the directions on your plate right now",
    helper:
      "One per line. Rough notes are fine. We are looking at the shape of what is in front of you, not the polish.",
    placeholder:
      "Example:\n- A productised positioning audit for early-stage founders\n- A monthly advisory for two existing clients\n- A short course on offer architecture",
    multiline: true,
    required: true,
  },
  {
    id: "most_alive",
    eyebrow: "Question 02 of 05",
    label: "Which of those feels most alive to you today",
    helper:
      "Energy is data. The one you would talk about over dinner without being asked. Write it in your own words.",
    placeholder: "Example: The positioning audit. It is the one I keep thinking about.",
    multiline: false,
    required: true,
  },
  {
    id: "feels_unclear",
    eyebrow: "Question 03 of 05",
    label: "What feels unclear about it right now",
    helper:
      "The bit you have been avoiding writing down. Pricing, positioning, audience, scope, capacity. Whatever it is, say it plainly.",
    placeholder:
      "Example: I am not sure who exactly it is for, and I keep oscillating between selling it as a one-off and as part of a retainer.",
    multiline: true,
    required: true,
  },
  {
    id: "imagined_audience",
    eyebrow: "Question 04 of 05 · Optional",
    label: "Who do you imagine this being for first",
    helper:
      "A specific person, role, or season of business. Skip if it is not clear yet, the Kit will help you write it later.",
    placeholder: "Example: A solo consultant 12 to 24 months in, charging by the hour and stuck there.",
    multiline: false,
    required: false,
  },
  {
    id: "progress_in_30_days",
    eyebrow: "Question 05 of 05 · Optional",
    label: "What would feel like real progress in the next 30 days",
    helper: "Quiet wins are valid. Less than three. Specific is better than impressive.",
    placeholder: "Example: A clear positioning brief I am willing to defend, and three paying pilots booked.",
    multiline: true,
    required: false,
  },
];

const initialAnswers = {
  ideas_listed: "",
  most_alive: "",
  feels_unclear: "",
  imagined_audience: "",
  progress_in_30_days: "",
};

export default function ClarityDiagnostic() {
  useSeo({
    title: "Free Clarity Check · BuildOnce Studio",
    description:
      "Five short questions. Five to ten minutes. Leave with a written reflection on what your own answers reveal. Free from BuildOnce Studio.",
  });
  // Fire a single page-view event on mount so /api/admin/metrics/export can
  // count how many people opened the Free Clarity Check page (as opposed to
  // just those who submitted a reflection). Fire-and-forget, never blocks.
  useEffect(() => {
    trackPageView("/clarity-diagnostic");
  }, []);
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState(initialAnswers);
  // P2: identity is no longer gate. The user completes the five
  // questions, sees their reflection immediately, and may optionally
  // enter an email in the post-reflection capture block to receive a
  // copy + join the buildonce-newsletter.
  const [phase, setPhase] = useState("questions"); // questions | submitting | reflection
  const [reflection, setReflection] = useState(null);
  const [submissionId, setSubmissionId] = useState(null);
  // Post-reflection capture state (P2).
  const [captureName, setCaptureName] = useState("");
  const [captureEmail, setCaptureEmail] = useState("");
  const [captureStatus, setCaptureStatus] = useState("idle"); // idle | sending | sent

  const step = STEPS[stepIndex];
  const value = answers[step.id];
  const canAdvance = !step.required || value.trim().length > 0;
  const isLastStep = stepIndex === STEPS.length - 1;

  const next = () => {
    if (!canAdvance) {
      toast.error("Add a short answer first, or skip to optional questions later.");
      return;
    }
    if (isLastStep) {
      // P2: last question complete → go straight to reflection.
      submitReflect();
      return;
    }
    setStepIndex((i) => i + 1);
  };

  const back = () => {
    if (stepIndex > 0) setStepIndex((i) => i - 1);
  };

  const submitReflect = async () => {
    if (phase === "submitting") return;
    setPhase("submitting");
    try {
      const { data } = await axios.post(`${API}/clarity-diagnostic/reflect`, {
        ideas_listed: answers.ideas_listed,
        most_alive: answers.most_alive,
        feels_unclear: answers.feels_unclear,
        imagined_audience: answers.imagined_audience || null,
        progress_in_30_days: answers.progress_in_30_days || null,
      });
      setReflection(data.reflection);
      setSubmissionId(data.submission_id);
      setPhase("reflection");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Something did not go through. Please try again.");
      setPhase("questions");
    }
  };

  const submitCapture = async (e) => {
    e.preventDefault();
    if (captureStatus === "sending" || captureStatus === "sent") return;
    if (!captureEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(captureEmail.trim())) {
      toast.error("A valid email address, please.");
      return;
    }
    if (!submissionId) return;
    setCaptureStatus("sending");
    try {
      await axios.post(`${API}/clarity-diagnostic/${submissionId}/capture`, {
        email: captureEmail.trim(),
        name: captureName.trim() || null,
      });
      setCaptureStatus("sent");
    } catch (err) {
      toast.error("Something did not go through. Please try again in a moment.");
      setCaptureStatus("idle");
    }
  };

  const progress = phase === "reflection" ? 100 : (stepIndex / STEPS.length) * 100;

  return (
    <div data-testid="clarity-diagnostic-page">
      <section className="border-b border-bos-ice">
        <div className="mx-auto max-w-[860px] px-6 lg:px-10 pt-24 lg:pt-28 pb-12">
          <Link to="/clarity-kit" className="inline-flex items-center gap-2 text-bos-primary bos-link-underline text-[13px]" data-testid="cd-back-link">
            <ArrowLeft size={13} /> Back to the Clarity Kit
          </Link>
          <p className="bos-eyebrow mt-8">Section 1 · Free</p>
          <h1 className="mt-4 text-3xl sm:text-4xl lg:text-[52px] font-medium text-bos-navy tracking-tight leading-[1.08]">
            Free Clarity <span className="text-bos-primary">Check.</span>
          </h1>
          <p className="mt-6 text-[16px] text-bos-charcoal/85 leading-[1.75]">
            Five short questions. Five to ten minutes. You leave with a written
            reflection on what your own answers reveal, and a clear sense of
            whether the full Kit will help.
          </p>

          <div className="mt-10" data-testid="cd-progress">
            <div className="h-[3px] w-full bg-bos-ice rounded-full overflow-hidden">
              <div
                className="h-full bg-bos-navy transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="mt-3 text-[11px] tracking-[0.14em] uppercase text-bos-charcoal/55">
              {phase === "reflection"
                ? "Reflection ready"
                : phase === "submitting"
                ? "Reading your answers"
                : `Question ${stepIndex + 1} of ${STEPS.length}`}
            </div>
          </div>

          {phase !== "reflection" && (
            <div
              data-testid="cd-ai-input-notice"
              className="mt-8 inline-flex items-start gap-3 max-w-2xl bg-bos-ice/70 border border-bos-light/60 rounded-sm px-4 py-3 text-[12px] text-bos-charcoal/80 leading-[1.6]"
            >
              <Sparkles size={14} className="mt-0.5 text-bos-primary shrink-0" />
              <span>
                Your responses will be reviewed using AI to generate a
                personalised reflection. Your answers are not shared with
                anyone outside BuildOnce Studio.
              </span>
            </div>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-[860px] px-6 lg:px-10 py-14 lg:py-20">
        {phase === "questions" && (
          <div data-testid={`cd-step-${step.id}`} className="bos-fade-up">
            <p className="text-[12px] tracking-[0.18em] uppercase text-bos-mid">{step.eyebrow}</p>
            <h2 className="mt-4 text-2xl lg:text-[32px] font-medium text-bos-navy leading-[1.18] tracking-tight">
              {step.label}
            </h2>
            <p className="mt-4 text-[14px] text-bos-charcoal/75 leading-[1.7] max-w-2xl">
              {step.helper}
            </p>

            <div className="mt-8">
              {step.multiline ? (
                <textarea
                  value={value}
                  onChange={(e) => setAnswers({ ...answers, [step.id]: e.target.value })}
                  rows={7}
                  placeholder={step.placeholder}
                  data-testid={`cd-input-${step.id}`}
                  className="w-full px-5 py-4 bg-bos-white border border-bos-ice focus:border-bos-mid outline-none rounded-sm text-[15px] leading-[1.7] resize-none"
                />
              ) : (
                <input
                  type="text"
                  value={value}
                  onChange={(e) => setAnswers({ ...answers, [step.id]: e.target.value })}
                  placeholder={step.placeholder}
                  data-testid={`cd-input-${step.id}`}
                  className="w-full px-5 py-4 bg-bos-white border border-bos-ice focus:border-bos-mid outline-none rounded-sm text-[15px]"
                />
              )}
            </div>

            <div className="mt-10 flex items-center justify-between">
              <button
                type="button"
                onClick={back}
                disabled={stepIndex === 0}
                data-testid="cd-back-btn"
                className="text-[13px] text-bos-charcoal/70 hover:text-bos-navy disabled:opacity-0 transition-colors"
              >
                ← Back
              </button>
              <button
                type="button"
                onClick={next}
                data-testid="cd-next-btn"
                className="inline-flex items-center gap-2 bg-bos-navy text-bos-white px-7 py-3.5 rounded-full hover:bg-bos-primary transition-colors text-[14px]"
              >
                {isLastStep ? "Finish & see your reflection" : "Next"}
                <ArrowRight size={14} />
              </button>
            </div>
          </div>
        )}

        {phase === "submitting" && (
          <div className="bos-fade-up text-bos-charcoal/70" data-testid="cd-submitting">
            <Sparkles size={18} className="text-bos-primary" />
            <p className="mt-4 text-[15px]">Reading your answers carefully…</p>
          </div>
        )}

        {phase === "reflection" && reflection && (
          <div className="bos-fade-up" data-testid="cd-reflection">
            <div className="inline-flex items-center gap-2 text-bos-primary">
              <CheckCircle2 size={16} />
              <span className="bos-eyebrow">Your reflection</span>
            </div>
            <h2 className="mt-4 text-2xl lg:text-[36px] font-medium text-bos-navy leading-[1.15] tracking-tight">
              What your answers reveal, in plain language.
            </h2>

            <div className="mt-10 space-y-8">
              <ReflectionBlock label="The pattern" body={reflection.pattern} />
              <ReflectionBlock label="The tension worth sitting with" body={reflection.tension} />
              <ReflectionBlock label="The question for next" body={reflection.next_question} />
              <ReflectionBlock label="What the full Kit unlocks" body={reflection.closing_invitation} accent />
            </div>

            <p
              data-testid="cd-ai-output-notice"
              className="mt-8 text-[12px] text-bos-charcoal/65 leading-[1.65] max-w-2xl"
            >
              This reflection was generated by AI using the answers you
              provided. It is a thinking tool, not professional advice.
            </p>

            <PostReflectionCapture
              submissionId={submissionId}
              name={captureName}
              email={captureEmail}
              status={captureStatus}
              onNameChange={setCaptureName}
              onEmailChange={setCaptureEmail}
              onSubmit={submitCapture}
            />

            <div className="mt-14 border-t border-bos-ice pt-10">
              <p className="bos-eyebrow">If this was useful</p>
              <h3 className="mt-3 text-2xl font-medium text-bos-navy leading-[1.2]">
                Continue into the full Clarity Kit.
              </h3>
              <p className="mt-3 text-[14px] text-bos-charcoal/80 leading-[1.7] max-w-xl">
                Sections 2 to 6 take you from "the one alive direction" to a
                positioning starter, an offer outline, and a 30-day clarity
                plan. The amount you pay for the Kit is credited toward the
                Diagnostic within 30 days.
              </p>
              <div className="mt-7 flex flex-wrap gap-4">
                <Link
                  to="/clarity-kit#reserve"
                  data-testid="cd-reflection-cta-kit"
                  className="inline-flex items-center gap-2 bg-bos-navy text-bos-white px-7 py-3.5 rounded-full hover:bg-bos-primary transition-colors text-[14px]"
                >
                  Get the Full Kit · SGD 97
                  <ArrowRight size={14} />
                </Link>
                <Link
                  to="/booking"
                  data-testid="cd-reflection-cta-diagnostic"
                  className="inline-flex items-center gap-2 border border-bos-navy text-bos-navy px-7 py-3.5 rounded-full hover:bg-bos-navy hover:text-bos-white transition-colors text-[14px]"
                >
                  Book the BuildOnce Diagnostic
                </Link>
              </div>

              {/* D.14 — cross-link to the free Clarity Conversation (AI-assisted
                  thinking partner). Sits below the Kit/Diagnostic CTA row so
                  it does not compete with the primary conversion action. */}
              <p
                className="mt-10 text-[14px] text-bos-charcoal/80 leading-[1.7]"
                data-testid="cd-crosslink-ai-demo"
              >
                Prefer to talk it through instead?{" "}
                <Link to="/clarity-ai-demo" className="text-bos-navy underline underline-offset-4 hover:text-bos-primary">
                  Try the Clarity Conversation.
                </Link>
              </p>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

const ReflectionBlock = ({ label, body, accent = false }) => (
  <div
    className={`grid lg:grid-cols-[1fr_3fr] gap-6 lg:gap-10 ${
      accent ? "bg-bos-ice/60 border border-bos-light/50 rounded-sm p-6 lg:p-8" : ""
    }`}
  >
    <div className="text-[11px] tracking-[0.18em] uppercase text-bos-mid pt-1">
      {label}
    </div>
    <p className="text-[16px] text-bos-charcoal/90 leading-[1.85]">{body}</p>
  </div>
);

// P2: Post-reflection email capture. The reflection is fully visible
// above (ungated). This block sits below the reflection and lets the
// user optionally opt in to receive a copy by email. On submit the
// backend sends the reflection and adds the contact to the
// buildonce-newsletter Resend Audience. Copy is verbatim from the
// revision brief. Nothing is withheld if the user does not submit.
const PostReflectionCapture = ({
  submissionId,
  name,
  email,
  status,
  onNameChange,
  onEmailChange,
  onSubmit,
}) => {
  if (!submissionId) return null;

  if (status === "sent") {
    return (
      <div
        data-testid="cd-capture-success"
        className="mt-12 bg-bos-ice/70 border border-bos-light/60 rounded-sm p-6 lg:p-7"
      >
        <div className="inline-flex items-center gap-2 text-bos-mid">
          <Mail size={14} />
          <span className="bos-eyebrow">Sent</span>
        </div>
        <h4 className="mt-3 text-[18px] font-medium text-bos-navy leading-[1.3]">
          On its way. Check your inbox in a moment.
        </h4>
        <p className="mt-3 text-[13px] text-bos-charcoal/75 leading-[1.7] max-w-2xl">
          If it does not arrive within ten minutes, check the promotions
          folder or write to <a href="mailto:info@buildoncestudio.com" className="text-bos-navy underline">info@buildoncestudio.com</a>.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="cd-capture-form"
      className="mt-12 bg-bos-white border border-bos-ice rounded-sm p-6 lg:p-7"
    >
      <div className="inline-flex items-center gap-2 text-bos-mid">
        <Mail size={14} />
        <span className="bos-eyebrow">Optional</span>
      </div>
      <h4 className="mt-3 text-[20px] lg:text-[22px] font-medium text-bos-navy leading-[1.3]">
        Want a copy of this?
      </h4>
      <p className="mt-3 text-[14px] text-bos-charcoal/80 leading-[1.7] max-w-2xl">
        Enter your email and I will send it to you, along with the occasional
        note from the studio. No sequences, and unsubscribing is easy.
      </p>

      <div className="mt-6 grid sm:grid-cols-2 gap-4 max-w-2xl">
        <div>
          <label
            htmlFor="cd-capture-name"
            className="block text-[12px] text-bos-charcoal/70 mb-1.5"
          >
            Your name (optional)
          </label>
          <input
            id="cd-capture-name"
            data-testid="cd-capture-name"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            className="w-full px-4 py-3 bg-bos-white border border-bos-ice focus:border-bos-mid outline-none rounded-sm text-[14px]"
          />
        </div>
        <div>
          <label
            htmlFor="cd-capture-email"
            className="block text-[12px] text-bos-charcoal/70 mb-1.5"
          >
            Email
          </label>
          <input
            id="cd-capture-email"
            data-testid="cd-capture-email"
            type="email"
            required
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            className="w-full px-4 py-3 bg-bos-white border border-bos-ice focus:border-bos-mid outline-none rounded-sm text-[14px]"
          />
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-4">
        <button
          type="submit"
          disabled={status === "sending"}
          data-testid="cd-capture-submit"
          className="inline-flex items-center gap-2 bg-bos-navy text-bos-white px-6 py-3 rounded-full hover:bg-bos-primary transition-colors disabled:opacity-60 text-[14px]"
        >
          {status === "sending" ? "Sending…" : "Send it to me"}
          <ArrowRight size={14} />
        </button>
        <span className="text-[12px] text-bos-charcoal/60">
          Or skip this. The reflection above is yours.
        </span>
      </div>

      <p className="mt-5 text-[11px] text-bos-charcoal/55 leading-[1.6] max-w-2xl">
        We use Resend to deliver the email and to hold your address on the
        buildonce-newsletter list. You can unsubscribe from any email we
        send. Details in our <Link to="/privacy-policy" className="underline hover:text-bos-navy">privacy policy</Link>.
      </p>
    </form>
  );
};
